import { observer } from 'mobx-react-lite'
import { FC } from 'react'
import ReactGA from 'react-ga'
import { useNavigate } from 'react-router-dom'
import ArpRegistry from '../../../features/arp-registry/ArpRegistry'
import { useAuth } from '../../hooks/useAuth'
import { styled } from '../../lib/stitches'
import { useArpMedias } from '../../queries/arpMedia'
import { useTreeState } from '../../stores/Root'
import { requiresUserLoggedIn } from '../AuthFlow/AuthFlowModal'
import { userIsLoggedIn } from '../core/Authentication'
import CenteredLoadingAnimation from '../core/CenteredLoadingAnimation'
import { Box, Column, Row } from '../core/Components'
import { SampleDrawerIcon, StemDrawerIcon } from '../core/icons'
import { QuestionMark } from '../core/icons/responsive-icons'
import withTooltip from '../core/withTooltip'
import { PUBLIC_NOTION_URL } from '../platform/constants'
import { ArpeggiLogo, BugIcon, DraftsIcon, HeartIcon, HeatIcon } from '../platform/icons'
import { ACTIVITY_BAR_WIDTH, HEADER_HEIGHT } from '../studio/Studio'
import DraftSelector from './activitybar/DraftSelector'
import FavoritesSelector from './activitybar/FavoritesSelector'
import FeaturedMedia from './activitybar/FeaturedMedia'
import SampleSelector from './activitybar/SampleSelector'
import StudioCloseDraftComponent from './activitybar/StudioCloseDraftComponent'
import StudioDeleteComponent from './activitybar/StudioDeleteComponent'
import StudioSaveComponent from './activitybar/StudioSaveComponent'
import StudioShareComponent from './activitybar/StudioShareComponent'
import { segmentStudioButton } from './StudioUtils'

export const ALLOW_STEM_AND_SONG_PREVIEW = false

export const ActivityBarContent = {
  SAMPLES: {
    title: 'Samples',
    component: <SampleSelector />
  },
  STEMS: {
    title: 'Discover Stems'
  },
  FAVORITES: {
    title: 'Favorites',
    component: <FavoritesSelector />
  },
  HEAT: {
    title: 'Featured',
    component: <FeaturedMedia />
  }
}

const ICON_SIZE = '20px'
const Container = withTooltip(
  styled('div', {
    color: '$gray01'
  }),
  'right'
)

interface StudioActivityBarIconProps {
  src?: string
  icon?: JSX.Element
  onClick?: (component: HTMLElement | any, title?: string) => void
  title: string
  component?: JSX.Element
  children?: JSX.Element | JSX.Element[]
  selectedActivityBarIcon?: any
}
export const StudioActivityBarIcon: FC<StudioActivityBarIconProps> = ({
  src,
  icon,
  onClick,
  title,
  component,
  children,
  selectedActivityBarIcon
}) => {
  return (
    <Container
      onClick={(e) => {
        if (onClick && component) onClick(component, title)
        else if (onClick) onClick(e)
      }}
      className={`mx-auto cursor-pointer d-flex justify-content-center align-items-center studio-activity-bar-component`}
      tooltipText={title}
      style={{ backgroundColor: selectedActivityBarIcon === title && 'var(--gray-7)' }}
    >
      {src && <img src={src} style={{ height: ICON_SIZE }} />}
      {icon}
      {children && children}
    </Container>
  )
}

interface StudioActivityBarProps {
  setSideDrawerContent: any
  pullOutSideDrawer: any
  selectedActivityBarIcon: any
}
const StudioActivityBar: FC<StudioActivityBarProps> = ({
  pullOutSideDrawer,
  selectedActivityBarIcon,
  setSideDrawerContent
}) => {
  const { studio } = useTreeState()
  const { user } = useAuth()
  const navigate = useNavigate()
  // Calling this here to cache the default sample IDs
  useArpMedias({ isDefaultSample: true, perPage: 1000 })

  const headerIcon = <ArpeggiLogo width="25px" height="25px" />

  const onCloseStudio = () => {
    navigate('/home')
  }
  return (
    <Column
      style={{
        left: '0',
        top: '0',
        height: '100vh',
        minWidth: ACTIVITY_BAR_WIDTH,
        backgroundColor: 'var(--gray-9)',
        zIndex: '3'
      }}
      horizontalCenter
    >
      {/* Top Icon */}
      <Row
        css={{ height: HEADER_HEIGHT, marginBottom: '45px', cursor: 'pointer' }}
        verticalCenter
        horizontalCenter
      >
        {user && studio.songChangedSinceSave && (
          <StudioCloseDraftComponent trigger={headerIcon} next={onCloseStudio} />
        )}
        {(!user || (user && !studio.songChangedSinceSave)) && (
          <Box onClick={onCloseStudio}>{headerIcon}</Box>
        )}
      </Row>

      {/* First Grouping of Components */}
      <div>
        <StudioActivityBarIcon
          title={ActivityBarContent.HEAT.title}
          onClick={(component, icon) => {
            pullOutSideDrawer(component, icon)
            segmentStudioButton('View Featured', 'studio activity bar')
          }}
          component={ActivityBarContent.HEAT.component}
          icon={<HeatIcon height={ICON_SIZE} width={ICON_SIZE} />}
          selectedActivityBarIcon={selectedActivityBarIcon}
        />
        {ArpRegistry.readyForStudioUse ? (
          <StudioActivityBarIcon
            title={ActivityBarContent.SAMPLES.title}
            onClick={(component, icon) => {
              pullOutSideDrawer(component, icon)
              segmentStudioButton('View Sample Library', 'studio activity bar')
            }}
            component={ActivityBarContent.SAMPLES.component}
            icon={<SampleDrawerIcon height={ICON_SIZE} width={ICON_SIZE} />}
            selectedActivityBarIcon={selectedActivityBarIcon}
          />
        ) : (
          <StudioActivityBarIcon title={ActivityBarContent.SAMPLES.title}>
            <CenteredLoadingAnimation small light />
          </StudioActivityBarIcon>
        )}
        {ArpRegistry.readyForStudioUse ? (
          <StudioActivityBarIcon
            title={ActivityBarContent.STEMS.title}
            onClick={() => {
              ReactGA.event({
                category: 'Studio',
                action: 'Clicked Discover Community Stems box'
              })
              segmentStudioButton('View Stems Library', 'studio activity bar')
              studio.setShowDiscoverStemsDrawer(!studio.showDiscoverStemsDrawer)
            }}
            icon={<StemDrawerIcon height={ICON_SIZE} width={ICON_SIZE} />}
          />
        ) : (
          <StudioActivityBarIcon title={ActivityBarContent.STEMS.title}>
            <CenteredLoadingAnimation small light />
          </StudioActivityBarIcon>
        )}
        {requiresUserLoggedIn(
          <StudioActivityBarIcon
            title={ActivityBarContent.FAVORITES.title}
            onClick={(component, icon) => {
              if (!userIsLoggedIn(user)) {
                return
              }
              segmentStudioButton('View Favorites', 'studio activity bar')
              pullOutSideDrawer(component, icon)
            }}
            component={ActivityBarContent.FAVORITES.component}
            icon={<HeartIcon height={ICON_SIZE} width={ICON_SIZE} />}
            selectedActivityBarIcon={selectedActivityBarIcon}
          />
        )}
      </div>

      {/* Divider */}
      <hr style={{ height: '2px', margin: '30px 10px 30px 10px', color: '#6F6F6F' }} />

      {/* Second Grouping of Components */}
      <div>
        <StudioSaveComponent />
        {requiresUserLoggedIn(
          <StudioActivityBarIcon
            title="Drafts"
            onClick={(component, icon) => {
              if (!userIsLoggedIn(user)) {
                return
              }
              segmentStudioButton('View Drafts', 'studio activity bar')
              pullOutSideDrawer(component, icon)
            }}
            component={<DraftSelector />}
            icon={<DraftsIcon height={ICON_SIZE} width={ICON_SIZE} />}
            selectedActivityBarIcon={selectedActivityBarIcon}
          />
        )}
        <StudioDeleteComponent />
        <StudioShareComponent />

        {/* Divider */}
        <hr style={{ height: '2px', margin: '30px 10px 30px 10px', color: '#6F6F6F' }} />

        <StudioActivityBarIcon
          title="FAQ"
          onClick={() => {
            segmentStudioButton('FAQ', 'studio activity bar')
            window.open(PUBLIC_NOTION_URL, '_blank')
          }}
          component={null}
          icon={<QuestionMark height={ICON_SIZE} width={ICON_SIZE} />}
        />

        <StudioActivityBarIcon
          title="Bug Report"
          onClick={() => {
            segmentStudioButton('Bug Report', 'studio activity bar')
            window.open(
              'https://form.asana.com/?k=_GdCwvs_FzU-OoN-ZqJ7HA&d=1201466248800239',
              '_blank'
            )
          }}
          component={null}
          icon={<BugIcon height={ICON_SIZE} width={ICON_SIZE} />}
        />
      </div>
    </Column>
  )
}

export default observer(StudioActivityBar)
